import React from 'react';
import Icon from '../../../components/AppIcon';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';

const NetWorthCard = ({ netWorthData, trend }) => {
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    })?.format(amount);
  };

  const currentNetWorth = netWorthData?.[netWorthData?.length - 1]?.value;
  const previousNetWorth = netWorthData?.[netWorthData?.length - 2]?.value;
  const change = currentNetWorth - previousNetWorth;
  const changePercent = ((change / previousNetWorth) * 100)?.toFixed(2);

  return (
    <div className="bg-card rounded-xl p-4 md:p-6 border border-border shadow-elevation-2">
      <div className="flex items-center justify-between mb-4 md:mb-6">
        <div>
          <h3 className="text-base md:text-lg font-semibold text-foreground mb-1">
            Net Worth
          </h3>
          <p className="text-xs md:text-sm text-muted-foreground">
            Total assets minus liabilities
          </p>
        </div>
        <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg ${
          change >= 0 ? 'bg-success/10 text-success' : 'bg-error/10 text-error'
        }`}>
          <Icon name={change >= 0 ? 'TrendingUp' : 'TrendingDown'} size={16} />
          <span className="text-xs md:text-sm font-semibold">
            {change >= 0 ? '+' : ''}{changePercent}%
          </span>
        </div>
      </div>
      <div className="mb-4 md:mb-6">
        <p className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-2">
          {formatCurrency(currentNetWorth)}
        </p>
        <p className="text-xs md:text-sm text-muted-foreground">
          {change >= 0 ? '+' : ''}{formatCurrency(change)} from last month
        </p>
      </div>
      <div className="h-48 md:h-64 w-full mb-4 md:mb-6" aria-label="Net Worth Trend Chart">
        <ResponsiveContainer width="100%" height="100%">
          <LineChart data={netWorthData}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
            <XAxis 
              dataKey="month" 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
            />
            <YAxis 
              stroke="var(--color-muted-foreground)"
              style={{ fontSize: '12px' }}
              tickFormatter={(value) => `$${(value / 1000)?.toFixed(0)}k`}
            />
            <Tooltip
              contentStyle={{
                backgroundColor: 'var(--color-card)',
                border: '1px solid var(--color-border)',
                borderRadius: '8px',
                fontSize: '14px'
              }}
              formatter={(value) => [formatCurrency(value), 'Net Worth']}
            />
            <Line 
              type="monotone" 
              dataKey="value" 
              stroke="var(--color-primary)" 
              strokeWidth={3}
              dot={{ fill: 'var(--color-primary)', r: 4 }}
              activeDot={{ r: 6 }}
            />
          </LineChart>
        </ResponsiveContainer>
      </div>
      <div className="grid grid-cols-2 gap-3 md:gap-4">
        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingUp" size={16} className="text-success" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Total Assets
            </span>
          </div>
          <p className="text-lg md:text-xl font-bold text-foreground">
            {formatCurrency(trend?.totalAssets)}
          </p>
        </div>

        <div className="p-3 md:p-4 bg-muted rounded-xl">
          <div className="flex items-center gap-2 mb-2">
            <Icon name="TrendingDown" size={16} className="text-error" />
            <span className="text-xs md:text-sm font-medium text-muted-foreground">
              Total Liabilities
            </span>
          </div>
          <p className="text-lg md:text-xl font-bold text-foreground">
            {formatCurrency(trend?.totalLiabilities)}
          </p>
        </div>
      </div>
    </div>
  );
};

export default NetWorthCard;